import React, { useCallback, useState } from 'react';
import { runInAction } from 'mobx';

import { store } from 'Models/Store';
import {
	IProgramStructureEntityAttributes,
	ProgramStructureEntity,
	RequirementEntity,
	RequiringStudyPlanStudyPlanRequirement,
} from 'Models/Entities';

import {
	GET_PROGRAM_STRUCTURES_WITH_REQUIREMENTS_FOR_PROGRAM,
} from 'Queries/GraphQlQueries/ProgramStructure/ProgramStructureQueries';

import useMountedQuery from 'Hooks/useMountedQuery';
import { useMounted } from 'Hooks/useMounted';

import Steps from 'Views/Components/Steps/Steps';
import { Display } from 'Views/Components/Button/Button';
import { useWizardContext } from 'Views/Components/Wizard/WizardContext';
import Wizard from 'Views/Components/Wizard/Wizard';
import Spinner from 'Views/Components/Spinner/Spinner';
import { SAVE_STATUS } from 'Views/Components/AutoSaveHeader/AutoSaveHeader';

import {
	IStudyPlanWizardData,
} from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizard';
import WizardSteps from 'Views/Pages/Frontend/StudyPlanWizard/WizardSteps';
import StudyPlanWizardHeader
	from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizardHeader';
import StudyPlanWizardSidebar
	from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizardSidebar';
import ComponentDropdown, {
	ComboboxAfterSave,
} from 'Views/Pages/Frontend/StudyPlanWizard/ProgramStructure/ComponentDropdown';
import ProgramStructureSelector from 'Views/Pages/Frontend/StudyPlanWizard/ProgramStructure/ProgramStructureSelector';
import { useRequirementIndex }
	from 'Views/Pages/Frontend/StudyPlanWizard/RequirementIndexContext';
import { selectPlanRequirement, updatePlanProgramStructure } from 'Queries/REST/StudyPlan/StudyPlanRequests';

const getExistingAssignedComponentRequirements = (
	component: RequirementEntity,
	studyPlanRequirements: RequiringStudyPlanStudyPlanRequirement[],
) => {
	// retrieve all requirement ids for this component
	const allComponentRequirementIds = component.requirements
		.map(requirement => requirement.id);

	// find the union between all ids for this component and those assigned to the study plan
	// there should only ever be one, as each combox allows only 1 to be selected
	const existingSelectedComponentRequirement = studyPlanRequirements
		.filter(studyPlanRequirementJoin => (
			allComponentRequirementIds
				.includes(studyPlanRequirementJoin.studyPlanRequirementId)
		));

	return existingSelectedComponentRequirement;
};

const getProgramStructures = (programVersionId: string) => store.apolloClient
	.query<{ programStructureEntitys: IProgramStructureEntityAttributes[] }>({
		query: GET_PROGRAM_STRUCTURES_WITH_REQUIREMENTS_FOR_PROGRAM,
		fetchPolicy: 'network-only',
		variables: {
			programVersionId,
		},
	});

const ProgramStructurePage = () => {
	const mounted = useMounted();

	const {
		autoSaveStatus,
		step,
		stepLabels,
		data,
		setAndSaveData,
		errors,
		nextStep,
		goToStep,
		clearErrors,
	} = useWizardContext<IStudyPlanWizardData>();

	const {
		setCurrentComponentId,
	} = useRequirementIndex();

	const [
		programStructures,
		setProgramStructures,
	] = useState<Array<ProgramStructureEntity>>();

	const tryRetrieveProgramStructures = useCallback(async () => {
		if (!data?.studyPlan?.programVersionId) {
			return;
		}

		return getProgramStructures(data?.studyPlan?.programVersionId);
	}, [data?.studyPlan?.programVersionId]);

	useMountedQuery(
		tryRetrieveProgramStructures,
		[tryRetrieveProgramStructures],
		response => {
			const responseData = response?.data;

			if (responseData) {
				const retrievedProgramStructures = responseData.programStructureEntitys
					.map(programStructureAttributes => new ProgramStructureEntity(programStructureAttributes));

				setProgramStructures(retrievedProgramStructures);
			}
		},
	);

	// eslint-disable-next-line max-len
	const isDropdownDataLoading = !data || !data.studyPlan || data?.fetchPlanRequestState === 'loading' || programStructures === undefined;

	const selectedProgramStructure = !isDropdownDataLoading && programStructures
		? programStructures
			.find(programStructure => programStructure.id === data?.studyPlan?.programStructureId)
		: undefined;

	/*
		When program structures are assigned
		- clear any attached components
		- assign and save newly selected program structure
	*/
	const updateStudyPlanProgramStructureAssociations = useCallback(
		async (newProgramStructureId: string) => {
			if (!data?.studyPlan) {
				throw new Error('Study Plan not found when assigning program structure');
			}
			if (programStructures === undefined) {
				throw new Error('Program structures not loaded when updating component');
			}

			const newProgramStructure = programStructures.find(
				programStructure => programStructure.id === newProgramStructureId,
			);

			if (!newProgramStructure) {
				throw new Error('Could not find new program structure');
			}

			const planRequirementJoins = await updatePlanProgramStructure(data.studyPlan.id, newProgramStructureId);

			data.studyPlan.studyPlanRequirements = planRequirementJoins?.data
				.map(reqJoin => new RequiringStudyPlanStudyPlanRequirement(reqJoin)) ?? [];

			runInAction(() => {
				if (!data?.studyPlan) {
					throw new Error('Study Plan not found when updating cached program structure');
				}

				data.studyPlan.programStructureId = newProgramStructureId;
				data.studyPlan.programStructure = newProgramStructure;
				// always clear the current plan selections
				data.studyPlan.planSelections = [];

				// clear current selected component
				setCurrentComponentId(undefined);

				setAndSaveData({ ...data });
				if (mounted.current) {
					clearErrors();
				}
			});
		},
		[clearErrors, data, mounted, programStructures, setAndSaveData, setCurrentComponentId],
	);

	/*
		When the study plan program structure is changed
		- remove any requirements belonging to the newly selected requirement's component
	*/
	const updateStudyPlanComponentRequirement = useCallback(async (componentId: string, requirementId) => {
		if (!data?.studyPlan) {
			throw new Error('Study Plan not found when updating study plan component');
		}

		if (programStructures === undefined) {
			throw new Error('Program structures not loaded when updating component');
		}

		const currentProgramStructure = programStructures.find(programStructure => (
			programStructure.id === data.studyPlan?.programStructureId
		));

		if (!currentProgramStructure) {
			throw new Error('Could not find current program structure');
		}

		const updatedPlanRequirements = await selectPlanRequirement(data.studyPlan.id, componentId, requirementId);

		data.studyPlan.studyPlanRequirements = updatedPlanRequirements?.data
			.map(reqJoin => new RequiringStudyPlanStudyPlanRequirement(reqJoin)) ?? [];

		// planned and completed courses must be cleared if the structure
		// changes to prevent active offerings belonging to requirements
		// not active for this plan
		data.studyPlan.planSelections = [];

		setAndSaveData({ ...data });
	}, [data, programStructures, setAndSaveData]);

	const onAfterChangeStructure: ComboboxAfterSave = newValue => {
		if (data?.studyPlan && newValue && 'value' in newValue && typeof newValue.value === 'string') {
			const newProgramStructureId = newValue.value;
			updateStudyPlanProgramStructureAssociations(newProgramStructureId);
		}
	};

	// Store the component id in a closure
	const onAfterChangeRequirement: (componentId: string) => ComboboxAfterSave = (
		componentId => (
			newValue => {
				if (data?.studyPlan && newValue && 'value' in newValue && typeof newValue.value === 'string') {
					const requirementId = newValue.value;
					updateStudyPlanComponentRequirement(componentId, requirementId);
				}

				setCurrentComponentId(undefined);
			}
		)
	);

	/**
	 * Generate Component Requirement selection dropdowns
	 * Only display dropdowns for Compdontns with OR conditions
	 * (All Requirements for AND condition Components are automatically selected)
	 */
	const generateComponentDropdowns = () => (
		selectedProgramStructure?.components
			.slice()
			.filter(component => component.condition === 'OR')
			.sort((a, b) => a.order - b.order)
			.map(component => {
				if (!data?.studyPlan) {
					throw new Error('Attempted to display dropdowns before Study Plan was loaded');
				}
				const onAfterChange = onAfterChangeRequirement(component.id);

				const existingAssignedRequirements = getExistingAssignedComponentRequirements(
					component,
					data!.studyPlan!.studyPlanRequirements,
				);

				const existingAssignedRequirement = existingAssignedRequirements.length > 0
					? existingAssignedRequirements[0].studyPlanRequirementId
					: undefined;

				return (
					<ComponentDropdown
						key={component.id}
						component={component}
						errors={errors}
						onAfterSelectRequirement={onAfterChange}
						initialSelectedRequirementId={existingAssignedRequirement}
					/>
				);
			}));

	return (
		<>
			<StudyPlanWizardHeader />
			<Wizard.Body>
				<StudyPlanWizardSidebar
					studyPlan={data?.studyPlan}
				/>
				<Steps
					currentStep={step}
					steps={stepLabels}
					onStepClick={goToStep}
					disabled={autoSaveStatus === SAVE_STATUS.SAVING}
				/>
				<Wizard.Main>
					<div className="content step-1">
						{
							isDropdownDataLoading
								? <Spinner />
								: (
									<ProgramStructureSelector
										selectedProgramStructure={selectedProgramStructure}
										programStructures={programStructures}
										studyPlan={data.studyPlan!}
										onAfterChange={onAfterChangeStructure}
										errors={errors.programStructureId}
									/>
								)
						}
						{generateComponentDropdowns()}
					</div>
					<WizardSteps
						rightButtons={[
							{
								title: 'Next',
								display: Display.Solid,
								icon: {
									icon: 'arrow-forward',
									iconPos: 'icon-right',
								},
								testId: 'next-btn',
								// DO NOT shorten this to `nextStep` as it will pass the onClick event to the function.
								// The function is not expecting an onClick event
								onClick: () => nextStep(),
								disabled: autoSaveStatus === SAVE_STATUS.SAVING,
								ariaLabel: 'navigate to next step',
							},
						]}
					/>
				</Wizard.Main>
			</Wizard.Body>
		</>
	);
};

export default ProgramStructurePage;
